// When a screen throws while rendering (an answer shaped differently from what
// it expects, say), show a plain-English note instead of a blank app. One
// boundary wraps each route, so the menu and the other screens keep working;
// another wraps the whole app as a last resort.
import { IonContent, IonPage } from "@ionic/react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props { children: ReactNode; where?: string; page?: boolean }
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`${this.props.where ?? "app"}: ${error.message}`, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const note = (
      <div className="signin">
        <div className="card" role="alert">
          <h1>{this.props.where ? `The ${this.props.where} screen hit a problem` : "The planner hit a problem"}</h1>
          <p>Something on this screen didn't load the way it should. Nothing you entered was sent. Reload to try again; if it keeps happening, tell Andrea or James what you were doing.</p>
          <p className="small muted">{error.message}</p>
          <button type="button" className="btn btn--primary btn--lg" onClick={() => window.location.reload()}>Reload</button>
        </div>
      </div>
    );
    return this.props.page ? <IonPage><IonContent>{note}</IonContent></IonPage> : note;
  }
}

/** A route's screen inside its own boundary. */
export const guarded = (where: string, Screen: () => JSX.Element) => function Guarded() {
  return <ErrorBoundary where={where} page><Screen /></ErrorBoundary>;
};
